import { rootMargin } from '@helpers/animate';
import { getSrcs } from '@helpers/image';
import { Device, until } from '@helpers/media';
import { formatNumberValue } from '@helpers/utils';
import { withMotionMax } from '@hoc/withMotionMax';
import { useInView } from '@hooks/useInView';
import IntroBlock from '@stories/Components/Misc/IntroBlock/IntroBlock';
import { animate, useReducedMotion } from 'framer-motion';
import * as React from 'react';
import S from './StatsList.styles';
const StatsList = (props) => {
    const [containerRef, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    let totalIndex = 0;
    /**
     * Mapping of rows to grid rows and indexes of first image in each row
     */
    const mapping = React.useMemo(() => {
        const indexes = [];
        const rows = [];
        if (props.rows && props.rows.length > 1) {
            let index = 0;
            let rowCount = 0;
            props.rows.forEach((row, i) => {
                if (row.type === 'five') {
                    rowCount = rowCount + 1;
                    rows.push(rowCount);
                    rowCount = rowCount + 1;
                    if (index === -1) {
                        index = row.items.findIndex((item) => item.src);
                        indexes.push(index);
                    }
                    else {
                        /**
                         * If the last row is not coupled, then check there are two images
                         * rom this row to go first on mobile or set none
                         */
                        if (i === props.rows.length - 1 && indexes.length % 2 === 0) {
                            index = row.items.findIndex((item) => item.image);
                            if (index !== -1) {
                                indexes.push(index);
                                indexes.push(-1);
                            }
                        }
                        else {
                            indexes.push(-1);
                        }
                    }
                }
                else {
                    rowCount = rowCount + 1;
                    rows.push(rowCount);
                    /**
                     * If the last row is not coupled, then don't set any first row images
                     */
                    if (i === props.rows.length - 1 && indexes.length % 2 === 0) {
                        return;
                    }
                    index = row.items.findIndex((item) => item.image);
                    indexes.push(index);
                }
            });
        }
        return { rows, indexes };
    }, [props.rows]);
    if (!props.rows || props.rows.length === 0) {
        return null;
    }
    return (React.createElement(S.Container, { ref: containerRef, variant: "charcoal" },
        React.createElement(IntroBlock, { title: props.title, titleSize: "sm" }),
        React.createElement(S.ItemGrid, null, props.rows.map((row, i) => (React.createElement(React.Fragment, { key: `stat-row-${row.id}` },
            row.items.map((item, index) => {
                if (item.stat) {
                    totalIndex++;
                }
                return (React.createElement(S.StatItem, { key: `stat-item-${row.id}-${index}`, style: {
                        '--borderColourStart': item.borderColourStart,
                        '--borderColourEnd': item.borderColourEnd,
                        '--col': getItemColumn(row, index),
                        '--imgRow': calculateImageRow(i),
                        '--row': mapping.rows[i] + (row.type === 'five' ? (index % 2 === 0 ? 0 : 1) : 0),
                    }, "data-first-img": index === mapping.indexes[i], "data-type": getItemType(row, index), "data-stat": !!item.stat }, item.image ? (React.createElement(React.Fragment, null, renderImage(item.image, inView))) : (React.createElement(StatsCardContent, { index: totalIndex, inView: inView, ...item }))));
            }),
            row.type === 'five' && (React.createElement(S.StatItem, { style: {
                    '--col': getItemColumn(row, 4),
                    '--imgRow': calculateImageRow(i),
                    '--row': mapping.rows[i],
                }, "data-type": "tall", "data-first-img": true, "data-stat": false }, renderImage(row.tallItem, inView)))))))));
    function renderImage(item, inView) {
        const srcs = getSrcs(item, 720, 375, 5, 504 / 262);
        return (React.createElement(S.ImageWrapper, { variants: {
                initial: { opacity: 0 },
                animate: { opacity: 1, transition: { delay: 0.66 } },
            }, initial: 'initial', animate: inView ? 'animate' : 'initial' },
            React.createElement(S.Image, { src: srcs.src, srcSet: srcs.srcSet, sizes: `${until(Device.TabletSmall)} 100vw, ${until(Device.DesktopLarge)} 41vw, 635px`, alt: item.altText, role: !item.altText ? 'presentation' : undefined, loading: "lazy" })));
    }
    function calculateImageRow(index) {
        let minus = 0;
        if (index % 2 !== 0) {
            minus = 1;
        }
        return (index - minus) * 4 + (index < 2 ? 1 : 0);
    }
    function getItemType(row, index) {
        if (row.type === 'three') {
            return undefined;
        }
        switch (row.layout) {
            case 's-t-w':
            case 's-w-t':
            case 't-s-w':
                if (index > 1) {
                    return 'wide';
                }
                break;
            case 'w-t-s':
            case 'w-s-t':
            case 't-w-s':
                if (index < 2) {
                    return 'wide';
                }
                break;
        }
        return 'standard';
    }
    function getItemColumn(row, index) {
        if (row.type === 'three') {
            return undefined;
        }
        switch (row.layout) {
            case 's-t-w':
                if (index < 2) {
                    return 1;
                }
                if (index < 4) {
                    return 8;
                }
                return 5;
            case 's-w-t':
                if (index < 2) {
                    return 1;
                }
                if (index < 4) {
                    return 5;
                }
                return 10;
            case 't-s-w':
                if (index < 2) {
                    return 4;
                }
                if (index < 4) {
                    return 8;
                }
                return 1;
            case 't-w-s':
                if (index < 2) {
                    return 4;
                }
                if (index < 4) {
                    return 9;
                }
                return 1;
            case 'w-t-s':
                if (index < 2) {
                    return 1;
                }
                if (index < 4) {
                    return 9;
                }
                return 6;
            case 'w-s-t':
                if (index < 2) {
                    return 1;
                }
                if (index < 4) {
                    return 6;
                }
                return 10;
            default:
                return undefined;
        }
    }
};
const StatsCardContent = ({ index, inView, ...item }) => {
    const prefersReducedMotion = useReducedMotion();
    const [count, setCount] = React.useState('0');
    const IxVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { delay: 0.2 } },
    };
    React.useEffect(() => {
        const value = item.stat ?? 0;
        if (!value || !inView) {
            return;
        }
        // Only perform animation if user hasn't doesn't prefer reduced motion
        if (prefersReducedMotion) {
            const number = formatNumberValue(value, 2);
            setCount(number);
        }
        else {
            const controls = animate(0, value, {
                type: 'tween',
                delay: index * 0.5,
                duration: 1.5,
                ease: 'easeOut',
                onUpdate(val) {
                    const decimals = value.toString().split('.')[1]?.length ?? 0;
                    const number = formatNumberValue(val, decimals);
                    setCount(number);
                },
            });
            return () => controls.stop();
        }
    }, [inView]);
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", null,
            item.prefix && (React.createElement(S.StatText, { variants: IxVariants, initial: 'initial', animate: count === formatNumberValue(item.stat) ? 'animate' : 'initial' }, item.prefix)),
            React.createElement(S.StatText, null, count),
            item.suffix && (React.createElement(S.StatText, { variants: IxVariants, initial: 'initial', animate: count === formatNumberValue(item.stat) ? 'animate' : 'initial' }, item.suffix)),
            React.createElement(S.Context, null, item.context)),
        React.createElement(S.Description, { dangerouslySetInnerHTML: { __html: item.description } })));
};
export default withMotionMax(StatsList);
